import {globalUser} from "./globals.js";
import {
    addNewCompanyStaffAndFacilities,
    getActiveCompetitorsForCompany,
    getCompanyRosterByType,
    getCompanyRosterCapacity,
    getCpuControlledCompanies
} from "./companies.js";
import {draftSuperstarToBrand, removeObjectBrand} from "./brands.js";
import {
    checkToAddCurrentObjectivesMessage,
    createCustomStartingCompanyCompetitorMessage,
    createIntroducePowerupsSandboxModeMessage, createIntroduceSandboxModeMessage,
    createJoinedCompanySuperstarMessage,
    createUpcomingYearlyDraftMessage
} from "./messages.js";
import {
    addCompanyMessages,
    addCompanyObjectives,
    getCompanyChampionships,
    getCompanyDraft,
    getCompanyGame,
    getCompanyObjectives,
    getCompanyRosters
} from "../models/company.js";
import {
    addDraftCompanies,
    addDraftRosters,
    Draft,
    getDraftCurrentPickRoster,
    setDraftCurrentPickRoster
} from "../models/draft.js";
import {Brands, db, Drafts, Rosters, Superstars} from "../models/models.js";
import {
    addRosterSuperstars,
    addRosterTagTeams,
    getRosterBrands,
    getRosterCompany,
    getRosterSuperstars,
    getRosterTagTeams
} from "../models/roster.js";
import {
    getSuperstarChampionshipsAll,
    getSuperstarDisplayData,
    removeSuperstarFactionRelationships,
    removeSuperstarManagerRelationships,
    vacateChampionships
} from "./superstars.js";
import {getCurrentGame, getRandomChoice, getRandomInt, objectInArray, shuffle, weightedChoice} from "./helpers.js";
import {getGameCurrentUserCompany, getGameMainUserCompany, getUserControlledCompanies} from "./games.js";
import {getSuperstarRoster} from "../models/superstar.js";
import {checkTalentRequestAction} from "./talentRequests.js";
import {generateCompanyObjectives} from "./objectives.js";
import {getTagTeamSuperstars, removeTagTeamSuperstars} from "../models/tagTeam.js";
import {transferTalentToRoster} from "./rosters.js";

async function createDraft(mainUserCompany, companies) {

    // Create draft event, add to company
    let draft = Draft();
    Drafts.insert(draft);
    draft.name = 'Roster Draft';
    await addDraftCompanies(draft, [mainUserCompany]);

    // Get rosters to add for draft
    let rosters = [];
    for (let company of companies) {
        rosters.push(await getCompanyRosterByType(company, 'main'));
    }

    // Add rosters to draft
    await addDraftRosters(draft, rosters)

    // Randomize roster order, add to rosters list
    shuffle(rosters);
    draft.rosterPickOrderIds = rosters.map(x => x.$loki);

    // Set first pick roster
    setDraftCurrentPickRoster(draft, rosters[0]);

    // Set draft result
    let result = {};
    for (let roster of rosters) {
        result[await getRosterCompanyName(roster)] = [];
    }
    draft.result = result;

    return draft;
}

async function createCustomCompanyDraft(mainUserCompany, draftSettings) {

    // Create draft event, add to company
    let draft = Draft();
    await Drafts.insert(draft);
    draft.name = 'Custom Company Draft';
    draft.type = 'user_only';
    draft.settings = draftSettings;
    await addDraftCompanies(draft, [mainUserCompany])

    // Add main roster to draft
    let mainRoster = await getCompanyRosterByType(mainUserCompany, 'main');
    await addDraftRosters(draft, [mainRoster])
    draft.rosterPickOrderIds = [mainRoster.$loki];
    setDraftCurrentPickRoster(draft, mainRoster);

    // Set draft result
    let result = {};
    result[mainUserCompany.name] = [];
    draft.result = result;

    return draft;
}

async function getDraftData(draft, company) {  // get_draft_data
    let data = {};
    let game = await getCompanyGame(company);
    let mainCompany = await getGameMainUserCompany(game);
    let companyChampionships = await getCompanyChampionships(company);
    if (draft.type === 'user_only') {
        const competitors = await getActiveCompetitorsForCompany(company, game);
        const competitorChampionships = (await Promise.all(competitors.map(x => getCompanyChampionships(x)))).flat();
        companyChampionships.push(...competitorChampionships);
    }

    data['result'] = JSON.parse(JSON.stringify(draft.result));
    data['ended'] = draft.ended;
    data['name'] = draft.name;
    data['type'] = draft.type;
    data['settings'] = draft.settings;
    data['uid'] = draft.$loki;

    // Get available/unavailable picks
    let {availableSuperstars, unavailableSuperstars} = await getPicksForDraft(draft, game, company, mainCompany);
    let sortedPicks = [...availableSuperstars, ...unavailableSuperstars].sort((a, b) => b.popularity - a.popularity);

    // Get picks data
    data['picksData'] = sortedPicks.map(x => {
        return {
            'name': x.name,
            'popularity': x.popularity,
            'age': x.age,
            'uid': x.$loki,
            'partTimer': x.partTimer,
            'retired': x.retired,
            'injured': x.injured > 0,
            'fullTimeManager': x.fullTimeManager,
            'championships': getSuperstarChampionshipsAll(x, companyChampionships),
            'unavailable': objectInArray(x, unavailableSuperstars)
        }
    });

    // Get brands/rosters
    if (draft.brands.length || draft.currentPickBrand) {
        data['brands'] = [...draft.brands];
        data['currentPickBrand'] = draft.currentPickBrand;
        data['isBrandDraft'] = true;
        let mainRoster = await getCompanyRosterByType(company, 'main');
        let rosterBrands = await getRosterBrands(mainRoster);
        data['brandColors'] = {};
        for (let x of rosterBrands) {
            data['brandColors'][x.name] = x.color;
        }
    } else {
        data['rosters'] = [];
        for (let x of draft.rosterPickOrderIds) {
            data['rosters'].push(await getRosterCompanyName(await Rosters.get(x)));
        }
        const currentPickRoster = await getDraftCurrentPickRoster(draft);
        data['currentPickCompany'] = await getRosterCompanyName(currentPickRoster);
        data['playerNames'] = {};
        let userControlledCompanies = await getUserControlledCompanies(game);
        for (let x of userControlledCompanies) {
            data['playerNames'][x.name] = x.playerName;
        }
        data['isBrandDraft'] = false;
        if (draft.type === 'user_only') {
            data['rosterCapacity'] = await getCompanyRosterCapacity(company, game);
        }
    }

    return data;
}

async function getRosterCompanyName(roster) {
    const rosterCompany = await getRosterCompany(roster);
    return rosterCompany.name;
}

async function getPicksForDraft(draft, game, company, mainCompany) {  // get_available_picks_for_draft
    // Return object of {availablePicks, unavailablePicks}
    let availableSuperstars = [];
    let unavailableSuperstars = [];

    if (draft.brands.length) {
        // Get main roster superstars
        let mainRoster = await getCompanyRosterByType(company, 'main');
        if (draft.type === 'shuffle') {

            // If 'shuffle' type draft, don't get superstars from current pick brand
            availableSuperstars = (await getRosterSuperstars(mainRoster)).filter(x => x.brandName !== draft.currentPickBrand);

            // Exclude already picked superstars
            let alreadyPickedNames = [...Object.values(draft.result)].flat();
            availableSuperstars = availableSuperstars.filter(x => !alreadyPickedNames.includes(x.name));
        } else {
            // If 'full' type draft, get superstars who aren't assigned to a brand
            availableSuperstars = (await getRosterSuperstars(mainRoster)).filter(x => !x.brandName);
        }
    } else {
        // For user only draft (custom company), get competitor/indie superstars based on draft settings
        if (draft.type === 'user_only') {

            // Get superstars from indie roster
            if (draft.settings['includeIndies']) {
                let indieRoster = await getCompanyRosterByType(mainCompany, 'indie');
                availableSuperstars.push(...(await getRosterSuperstars(indieRoster)));
            }

            // Get competitor superstars
            if (draft.settings['includeCompanyRosters']) {
                let competitorsSuperstars = [];
                for (let competitor of (await getActiveCompetitorsForCompany(company, game))) {
                    let rosters = (await getCompanyRosters(competitor)).filter(x => ['main', 'training'].includes(x.type));
                    for (let roster of rosters) {
                        competitorsSuperstars.push(...(await getRosterSuperstars(roster)))
                    }
                }

                // Get protected picks
                if (draft.settings['protectCompanyTalent']) {
                    if (draft.protectedSuperstarsIds.length) {
                        unavailableSuperstars = competitorsSuperstars.filter(x => draft.protectedSuperstarsIds.includes(x.$loki));
                    } else {
                        unavailableSuperstars = getProtectedDraftPicks(competitorsSuperstars);
                        draft.protectedSuperstarsIds = unavailableSuperstars.map(x => x.$loki);
                        db.saveDatabase();
                    }
                    competitorsSuperstars = competitorsSuperstars.filter(x => !objectInArray(x, unavailableSuperstars));
                }
                availableSuperstars.push(...competitorsSuperstars);
            }
        }
        else {
            // For main rosters draft, get superstars from indie available roster
            let indieAvailableRoster = await getCompanyRosterByType(mainCompany, 'indieAvailable');
            availableSuperstars = await getRosterSuperstars(indieAvailableRoster);
        }
    }

    return {availableSuperstars, unavailableSuperstars};
}

function getProtectedDraftPicks(superstars) {  // get_protected_draft_picks
    let protectedPicks = [];

    // Get superstar weights
    let superstarWeights = [];
    for (let s of superstars) {
        let weight = Math.pow((s.popularity / 100), 3);
        if (s.popularity >= 800) {
            weight *= 100;
        } else if (s.popularity >= 600) {
            weight *= 30;
        }
        if (s.hot) {
            weight *= 50;
        }
        if (s.traits.includes('Loyal')) {
            weight *= 100;
        }
        if (s.traits.includes('Rebel')) {
            weight *= 0.1;
        }
        if (s.retired) {
            weight *= 0.25;
        }
        superstarWeights.push([s, Math.floor(weight)]);
    }

    // Get protected picks
    let numberOfPicks = Math.floor(superstars.length * 0.3);
    for (let x = 0; x < numberOfPicks; x++) {
        let pick = weightedChoice(superstarWeights);
        protectedPicks.push(pick);
        superstarWeights = superstarWeights.filter(x => x[0].$loki !== pick.$loki);
    }

    return protectedPicks;
}

async function checkYearlyDraft(company, currentMonth) {  // check_yearly_draft
    let data = {};
    let draftSettings = company.settings['yearly_brand_draft_settings'];

    // Check if yearly draft enabled, in the right month, existing draft not present
    let draftCheck = false;
    let companyDraft = await getCompanyDraft(company);
    if (globalUser.paid && draftSettings && draftSettings['run'] && draftSettings['month'] &&
        !['mars', 'passion'].includes(company.storyName)) {
        let draftMonth = parseInt(draftSettings['month']);
        if (draftMonth === currentMonth) {
            // Run draft
            if (!companyDraft || companyDraft.ended) {
                draftCheck = true;
            }
        } else if (getMonthsIndexDifference(draftMonth, currentMonth) === 1) {
            // Add message about upcoming yearly draft one month before it happens
            let newMessage = await createUpcomingYearlyDraftMessage(company);
            await addCompanyMessages(company, [newMessage]);
        }
    }

    if (draftCheck) {
        // Get brands
        let brands = [];
        for (let brandUid of draftSettings['brandUids']) {
            let brand = await Brands.get(brandUid);
            if (brand) {
                brands.push(brand);
            }
        }

        // Create draft event
        if (brands.length > 0) {
            let mainRoster = await getCompanyRosterByType(company, 'main');
            await createBrandDraft(company, brands, draftSettings['type'], (await getRosterSuperstars(mainRoster)));
        }
    }

    return data;
}

function getMonthsIndexDifference(firstMonth, secondMonth) {  // get_months_index_difference
    // Return difference between month indexes (0-11)
    let monthsDifference;
    if (firstMonth < secondMonth) {
        monthsDifference = (firstMonth + 12) - secondMonth;
    } else {
        monthsDifference = firstMonth - secondMonth;
    }
    return monthsDifference;
}

async function createBrandDraft(company, brands, draftType, rosterSuperstars) {  // create_brand_draft
    // Create draft event, add to company
    let draft = Draft();
    await Drafts.insert(draft);
    draft.name = draftType === 'full' ? 'Brand Draft' : 'Brand Shuffle';
    draft.type = draftType;  // 'shuffle' or 'full'
    await addDraftCompanies(draft, [company]);

    // Add brands
    draft.brands = brands.map(x => x.name);

    // Set first pick brand
    draft.currentPickBrand = brands[0].name;

    // If full draft, remove brands from all superstars, reset brand overness
    if (draftType === 'full') {
        for (let superstar of rosterSuperstars) {
            removeObjectBrand(superstar);
        }
        for (let brand of brands) {
            brand.overness = 45;
        }
    }

    // Set draft result
    let result = {};
    for (let brand of draft.brands) {
        result[brand] = [];
    }
    draft.result = result;

    return draft;
}

async function getDraftSuperstarInfo({draftUid, superstarUid}) {  // get_superstar_data
    const superstar = await Superstars.get(superstarUid);
    const draft = await Drafts.get(draftUid);
    let data = await getSuperstarDisplayData({superstar, getTeamsAndFactions: true, getTagTeamPartners: true});

    // Don't show 'limited appearances' for retired part timers
    if (superstar.partTimer && superstar.retired) {
        data['partTimer'] = false;
    }

    // Get company name
    if (draft && draft['type'] === 'user_only') {
        let superstarRoster = await getSuperstarRoster(superstar);
        if (superstarRoster) {
            if (['indie', 'indieAvailable'].includes(superstarRoster.type)) {
                data['companyName'] = 'Indies';
            }
            else {
                let company = await getRosterCompany(superstarRoster);
                if (company) {
                    data['companyName'] = company.name;
                }
            }
        }
    }

    return data;
}

async function addUserDraftPick({superstarUid, draftUid, random} = {}) {  // add_user_pick_to_draft
    let data = {};
    let game = await getCurrentGame();
    let userCompany = await getGameCurrentUserCompany(game);
    let mainCompany = await getGameMainUserCompany(game);
    let draft = await Drafts.get(draftUid);
    let draftRoster = await getDraftCurrentPickRoster(draft);
    let draftRosterCompany = await getRosterCompany(draftRoster);
    let {availableSuperstars} = await getPicksForDraft(draft, game, userCompany, mainCompany);

    // Check for roster capacity
    let capacity = getCompanyRosterCapacity(draftRosterCompany, game);
    let size = (await getRosterSuperstars(draftRoster)).filter(x => !x.retired && !x.dead).length;
    if (size >= capacity) {
        data['error'] = 'max-capacity';
        data['capacity'] = capacity;
        return data;
    }

    // Check for available superstars
    if (!availableSuperstars.length) {
        data['error'] = 'no-superstars-available';
        return data;
    }

    // Find superstar
    let superstar;
    if (superstarUid) {
        superstar = await Superstars.get(superstarUid);
        if (!superstar) {
            data['error'] = 'superstar-not-found';
            return data;
        }
        if (!objectInArray(superstar, availableSuperstars)) {
            data['error'] = 'superstar-not-available';
            return data;
        }
    } else if (random) {
        superstar = await simulateDraftPick(availableSuperstars);
    }

    // Check if it's user's pick
    if (!draftRosterCompany.userControlled) {
        data = await processDraftPick(draft, availableSuperstars);
        db.saveDatabase();
        data['error'] = 'not-user-turn';
        return data;
    }

    // Process draft pick
    await processDraftPick(draft, availableSuperstars, superstar);

    db.saveDatabase();

    // Get draft result
    data['draftData'] = await getDraftData(draft, userCompany);

    // Get random pick data
    if (random) {
        data['randomPick'] = superstar.name;
        data['picksData'] = availableSuperstars.map(x => x.name);
    }

    return data;
}

async function addBrandDraftPick({superstarUid, draftUid, random} = {}) {  // add_brand_draft_pick
    let data = {};
    let game = await getCurrentGame();
    let userCompany = await getGameCurrentUserCompany(game);
    let mainCompany = await getGameMainUserCompany(game);
    let draft = await Drafts.get(draftUid);
    let mainRoster = await getCompanyRosterByType(userCompany, 'main');
    let rosterBrands = await getRosterBrands(mainRoster);
    let companyChampionships = await getCompanyChampionships(userCompany);

    // Check for available superstars
    let {availableSuperstars} = await getPicksForDraft(draft, game, userCompany, mainCompany);
    if (!availableSuperstars.length) {
        data['error'] = 'no-superstars-available';
        return data;
    }

    // Find superstar
    let superstar;
    if (superstarUid) {
        superstar = await Superstars.get(superstarUid);
        if (!superstar) {
            data['error'] = 'superstar-not-found';
            return data;
        }
        if (!objectInArray(superstar, availableSuperstars)) {
            data['error'] = 'superstar-not-available';
            return data;
        }
    } else if (random) {
        superstar = await simulateDraftPick(availableSuperstars);
    }

    // Check talent request
    let selectedBrand = rosterBrands.find(y => y.name === draft.currentPickBrand);
    if (selectedBrand) {
        let requestData = {'brandId': selectedBrand.$loki};
        await checkTalentRequestAction({company: userCompany, requestType: 'change_brand', talent: superstar,
            data: requestData});
    }

    // Process draft pick
    await processBrandDraftPick(draft, availableSuperstars, superstar, companyChampionships);

    db.saveDatabase();

    // Get draft result
    data['draftData'] = await getDraftData(draft, userCompany);

    // Get random pick data
    if (random) {
        data['randomPick'] = superstar.name;
        data['picksData'] = availableSuperstars.map(x => x.name);
    }

    return data;
}

async function simulateDraftPicks({draftUid} = {}) {  // simulate_draft_picks
    let data = {};
    let game = await getCurrentGame();
    let userCompany = await getGameCurrentUserCompany(game);
    let mainCompany = await getGameMainUserCompany(game);
    let draft = await Drafts.get(draftUid);

    // Simulate rest of draft
    let {availableSuperstars} = await getPicksForDraft(draft, game, userCompany, mainCompany);
    await processDraftPick(draft, availableSuperstars, null, true);

    db.saveDatabase();

    data['draftData'] = await getDraftData(draft, userCompany);

    return data;
}

async function skipBrandDraftTurn({draftUid} = {}) {  // skip_brand_draft_turn
    let data = {};
    let game = await getCurrentGame();
    let userCompany = await getGameCurrentUserCompany(game);
    let draft = await Drafts.get(draftUid);

    // Get next pick brand
    draft.currentPickBrand = getNextDraftPickBrand(draft);

    db.saveDatabase();

    data['draftData'] = await getDraftData(draft, userCompany);

    return data;
}

async function endBrandDraftTurn({draftUid} = {}) {  // end_brand_draft_turn
    let data = {};
    let game = await getCurrentGame();
    let userCompany = await getGameCurrentUserCompany(game);
    let draft = await Drafts.get(draftUid);
    let draftBrand = draft.currentPickBrand;

    // Remove brand from draft if draft has brands
    if (draft.brands.length) {
        draft.currentPickBrand = getNextDraftPickBrand(draft);
        draft.brands = draft.brands.filter(x => x !== draftBrand);
    }

    // End draft if no more brands present
    if (!draft.brands.length) {
        draft.ended = true;
        draft.postEndRun = true;
    }

    db.saveDatabase();

    data['draftData'] = await getDraftData(draft, userCompany);

    return data;
}

async function endDraftTurn({draftUid} = {}) {  // end_draft_turn
    let data = {};
    let game = await getCurrentGame();
    let userCompany = await getGameCurrentUserCompany(game);
    let mainCompany = await getGameMainUserCompany(game);
    let indieRoster = await getCompanyRosterByType(mainCompany, 'indie');
    let indieAvailableRoster = await getCompanyRosterByType(mainCompany, 'indieAvailable');
    let draft = await Drafts.get(draftUid);
    let draftRoster = await getDraftCurrentPickRoster(draft);
    let draftRosterCompany = await getRosterCompany(draftRoster);

    // Check for minimum picks
    let minimumPicks = 10;
    if (!draft.ended && (await getRosterSuperstars(draftRoster)).length < minimumPicks) {
        data['error'] = 'minimum-picks';
        data['minimumPicks'] = minimumPicks;
        return data;
    }

    // Remove user roster from draft if draft has rosters
    if (draftRosterCompany.userControlled && draft.rosterPickOrderIds.length) {
        setDraftCurrentPickRoster(draft, (await getNextDraftPickRoster(draft)));
        draft.rosterPickOrderIds = draft.rosterPickOrderIds.filter(x => x !== draftRoster.$loki);
    }

    // Process other picks
    let {availableSuperstars} = await getPicksForDraft(draft, game, userCompany, mainCompany);
    await processDraftPick(draft, availableSuperstars);

    if (draft.ended) {
        let mainCompanyObjectivesAdded = (await getCompanyObjectives(mainCompany)).length;
        let userControlledCompanies = await getUserControlledCompanies(game);

        for (let company of userControlledCompanies) {

            // Get main roster superstars
            let mainRoster = await getCompanyRosterByType(company, 'main');
            let mainRosterSuperstars = await getRosterSuperstars(mainRoster);

            // Add staff and facilities messages
            await addNewCompanyStaffAndFacilities(company, mainRosterSuperstars, indieAvailableRoster, game.settings);

            // Add unlimited artifacts message
            if (game.settings['unlock_all_items']) {
                let newMessage = await createIntroducePowerupsSandboxModeMessage(company, game);
                await addCompanyMessages(company, [newMessage]);
            }

            // Add sandbox mode message
            if (game.settings['sandbox_mode_enabled']) {
                let newMessage = await createIntroduceSandboxModeMessage(0, 0, company.ownerName, game.settings);
                await addCompanyMessages(company, [newMessage]);
            }

            // If custom starting company draft, add superstar joined / competitor messages
            if (draft.type === 'user_only') {
                let topSuperstars = [...mainRosterSuperstars].sort((a, b) => b.popularity - a.popularity).slice(0, 3);
                let newMessage = await createJoinedCompanySuperstarMessage(getRandomChoice(topSuperstars),
                    game.monthCount, game.year);
                await addCompanyMessages(company, [newMessage]);

                let competitors = await getCpuControlledCompanies(company, game);
                if (competitors.length) {
                    let newMessage = await createCustomStartingCompanyCompetitorMessage(getRandomChoice(competitors),
                        game.monthCount, game.year);
                    await addCompanyMessages(company, [newMessage]);
                }
            }

            // Add objectives
            if (!['mars', 'passion'].includes(company.storyName)) {
                let objectives = await generateCompanyObjectives(company, game);
                await addCompanyObjectives(company, objectives);
            }
        }

        if (!mainCompanyObjectivesAdded) {

            // Transfer teams for user only draft
            if (draft.type === 'user_only') {

                // Get all draft rosters
                let allDraftRosters = [];
                if (draft.settings['includeIndies']) {
                    allDraftRosters.push((await getCompanyRosterByType(mainCompany, 'indie')));
                }
                if (draft.settings['includeCompanyRosters']) {
                    for (let competitor of (await getActiveCompetitorsForCompany(userCompany, game))) {
                        let competitorRosters = (await getCompanyRosters(competitor)).filter(x => ['main', 'training'].includes(x.type));
                        allDraftRosters.push(...competitorRosters);
                    }
                }

                // Go through teams
                for (let roster of allDraftRosters) {
                    for (let tagTeam of (await getRosterTagTeams(roster))) {
                        // If all team superstars moved to another roster, move team
                        let tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);
                        let firstMemberRoster = await getSuperstarRoster(tagTeamSuperstars[0]);
                        let foundInRoster = firstMemberRoster;
                        for (const tagTeamSuperstar of tagTeamSuperstars) {
                            let memberRoster = await getSuperstarRoster(tagTeamSuperstar);
                            if (!memberRoster || memberRoster.$loki !== firstMemberRoster.$loki) {
                                foundInRoster = null
                            }
                        }
                        if (foundInRoster) {
                            if (foundInRoster.$loki !== roster.$loki) {
                                await addRosterTagTeams(foundInRoster, [tagTeam])
                            }
                        } else {
                            tagTeam.rosterId = null;
                            await removeTagTeamSuperstars(tagTeam);
                        }
                    }
                }
            }
            else {
                // Transfer indie teams
                for (let tagTeam of (await getRosterTagTeams(indieAvailableRoster))) {
                    // If all team superstars moved to another roster, move team
                    let tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);
                    let firstMemberRoster = await getSuperstarRoster(tagTeamSuperstars[0]);
                    let foundInRoster = firstMemberRoster;
                    for (const tagTeamSuperstar of tagTeamSuperstars) {
                        let memberRoster = await getSuperstarRoster(tagTeamSuperstar);
                        if (!memberRoster || memberRoster.$loki !== firstMemberRoster.$loki) {
                            foundInRoster = null
                        }
                    }
                    if (foundInRoster) {
                        if (foundInRoster.$loki !== indieAvailableRoster.$loki) {
                            await addRosterTagTeams(foundInRoster, [tagTeam])
                        }
                    } else {
                        tagTeam.rosterId = null;
                        await removeTagTeamSuperstars(tagTeam);
                    }
                }

                // Move all draft (indie available) superstars/tagteams to indie roster
                for (let s of (await getRosterSuperstars(indieAvailableRoster))) {
                    await addRosterSuperstars(indieRoster, [s])
                }
                for (let t of (await getRosterTagTeams(indieAvailableRoster))) {
                    await addRosterTagTeams(indieRoster, [t])
                }
            }

            // Make all indies available if sandbox mode
            if (game.settings['all_indies_available']) {
                for (let s of (await getRosterSuperstars(indieRoster))) {
                    await addRosterSuperstars(indieAvailableRoster, [s])
                }
                for (let t of (await getRosterTagTeams(indieRoster))) {
                    await addRosterTagTeams(indieAvailableRoster, [t])
                }
            }
            // Make three indie superstar available if not sandbox mode
            else {
                let indieSuperstars = await getRosterSuperstars(indieRoster);
                for (let i = 0; i < 3; i++) {
                    if (indieSuperstars.length) {
                        const talentToTransfer = getRandomChoice(indieSuperstars);
                        let transferredToIndies = await transferTalentToRoster(talentToTransfer, indieAvailableRoster);
                        indieSuperstars = indieSuperstars.filter(x => !objectInArray(x, transferredToIndies));
                    }
                }
            }
        }

        draft.postEndRun = true;
    }

    db.saveDatabase();

    data['draftData'] = await getDraftData(draft, userCompany);

    return data;
}

async function processDraftPick(draft, superstars, pick = null, simulating = false) {  // process_draft_pick
    let data = {};
    let game = await getCurrentGame();
    let draftRoster = await getDraftCurrentPickRoster(draft);
    let draftRosterCompany = await getRosterCompany(draftRoster);

    // If no picks or rosters left, end draft
    if (!superstars.length || !draft.rosterPickOrderIds.length) {
        draft.ended = true;
        return data;
    }

    // Return if user pick and if not simulating
    if (draftRosterCompany.userControlled && !pick && !simulating) {
        return data;
    }

    // Simulate pick for competitor
    if (!draftRosterCompany.userControlled || simulating) {
        pick = await simulateDraftPick(superstars);

        // Remove roster from draft if near capacity, get next pick roster
        let capacity = await getCompanyRosterCapacity(draftRosterCompany, game);
        let size = await getRosterSuperstars(draftRoster).filter(x => !x.retired && !x.dead).length;
        let minimum = Math.floor(capacity * 0.8);
        if (size >= getRandomInt(minimum, capacity)) {
            setDraftCurrentPickRoster(draft, (await getNextDraftPickRoster(draft)));
            draft.rosterPickOrderIds = draft.rosterPickOrderIds.filter(x => x !== draftRoster.$loki);
            return processDraftPick(draft, superstars, null, simulating);
        }
    }

    // Remove championships/managers/factions/brands
    if (draft.type === 'user_only') {
        let pickRoster = await getSuperstarRoster(pick);
        let pickCompany = await getRosterCompany(pickRoster);
        if (pickCompany && pickCompany !== draftRosterCompany) {
           await vacateChampionships({superstar: pick, championships: (await getCompanyChampionships(pickCompany)),
               month: 0, year: 0});
        }
        await removeSuperstarManagerRelationships(pick);
        await removeSuperstarFactionRelationships(pick, 0, 0);
        pick.brandName = null;
    }

    // Move superstar to draftRoster
    await addRosterSuperstars(draftRoster, [pick]);
    superstars = superstars.filter(x => x.$loki !== pick.$loki);

    // Add pick to draft result
    await updateDraftResult(draft, pick);

    // Get next pick roster
    setDraftCurrentPickRoster(draft, (await getNextDraftPickRoster(draft)));

    // Run next pick
    return processDraftPick(draft, superstars, null, simulating);
}

async function getNextDraftPickRoster(draft) {  // get_next_draft_pick_roster
    let order = draft.rosterPickOrderIds;

    // Get next roster id in order
    let rosterIndex = order.indexOf((await getDraftCurrentPickRoster(draft))?.$loki);
    let rosterId;
    if (rosterIndex === order.length - 1) {
        rosterId = order[0];
    } else {
        rosterId = order[rosterIndex + 1];
    }

    // Get next roster by id
    return Rosters.get(rosterId);
}

async function processBrandDraftPick(draft, superstars, pick = null, companyChampionships = null) {  // process_brand_draft_pick
    let data = {};
    let draftBrand = draft.currentPickBrand;

    // If no picks left, end draft
    if (!superstars.length) {
        draft.ended = true;
        return data;
    }

    // Draft superstar
    draftSuperstarToBrand(pick, draftBrand, companyChampionships);

    // Add pick to draft result
    await updateDraftResult(draft, pick);

    // Get next pick brand
    draft.currentPickBrand = getNextDraftPickBrand(draft);

    return true;
}

function getNextDraftPickBrand(draft) {  // get_next_draft_pick_brand
    let draftBrands = draft.brands;

    // Get next brand in order
    let brandIndex = draftBrands.indexOf(draft.currentPickBrand);
    let nextBrand;
    if (brandIndex === draftBrands.length - 1) {
        nextBrand = draftBrands[0];
    } else {
        nextBrand = draftBrands[brandIndex + 1];
    }

    return nextBrand;
}

async function updateDraftResult(draft, pick) {
    // Get key
    let key;
    if (draft.brands.length) {
        // Brand name
        key = draft.currentPickBrand;
    } else {
        // Current roster's company name
        let currentRoster = await getDraftCurrentPickRoster(draft);
        key = await getRosterCompanyName(currentRoster);
    }

    // Add pick to draft result
    draft.result[key].push(pick.name);

    return draft.result;
}

function simulateDraftPick(superstars) {  // simulate_draft_pick
    let superstarWeights = getSuperstarWeights(superstars);
    return weightedChoice(superstarWeights);
}

function getSuperstarWeights(superstars) {  // get_superstar_weights
    // Get weights for top superstars by popularity, ensuring that active superstars are included
    let superstarWeights = [];
    let numberOfSuperstars = 8;

    let sortedSuperstars = [...superstars].sort((a, b) => b.popularity - a.popularity);
    let activeSuperstars = sortedSuperstars.filter(x => !x.retired);

    // Get index of last active superstar in sorted superstars, use to get top superstars
    let lastActiveSuperstarIndex;
    if (activeSuperstars.length > numberOfSuperstars) {
        lastActiveSuperstarIndex = sortedSuperstars.indexOf(activeSuperstars[numberOfSuperstars]);
    } else if (activeSuperstars.length > 0) {
        lastActiveSuperstarIndex = sortedSuperstars.indexOf(activeSuperstars[activeSuperstars.length - 1]);
    } else {
        lastActiveSuperstarIndex = numberOfSuperstars;
    }
    let topSuperstars = sortedSuperstars.slice(0, lastActiveSuperstarIndex + 1);

    topSuperstars.forEach(s => {
        // Set weight
        let weight = (Math.pow(s.popularity / 100, 6)) / 10000;
        if (s.workrate >= 3) {
            weight *= 3;
        }
        if (s.charisma >= 3) {
            weight *= 3;
        }
        // Part timers less likely
        if (s.partTimer) {
            weight *= 0.6;
        }
        // Retired less likely
        if (s.retired) {
            weight *= 0.02;
        }
        // Manager less likely
        if (s.fullTimeManager) {
            weight *= 0.2;
        }
        // Hot/stale
        if (!s.retired) {
            if (s.hot) {
                weight *= 10;
            }
            if (s.stale) {
                weight *= 0.1;
            }
        }
        superstarWeights.push([s, Math.floor(weight)]);
    });

    return superstarWeights;
}

export {checkYearlyDraft, getDraftData, getDraftSuperstarInfo, addUserDraftPick, addBrandDraftPick, endBrandDraftTurn,
    endDraftTurn, simulateDraftPicks, skipBrandDraftTurn, processDraftPick, createDraft, createCustomCompanyDraft,
    createBrandDraft}